/**
 * (c) 2021, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { createContext, useContext } from 'react';

export interface Language {
  id: string;
  name: string;
  enName: string;
}

export const fallbackLocale = 'en';

const allLanguages: Language[] = [
  {
    id: 'en',
    name: 'English',
    enName: 'English',
  },
  {
    id: 'es-es',
    name: 'Español',
    enName: 'Spanish',
  },
];

export const supportedLanguages: Language[] = allLanguages;

export const minimumFontSize = 4;
export const maximumFontSize = 154;
export const fontSizeStep = 3;

export type ParameterHelpOption = 'automatic' | 'manual';
export const parameterHelpOptions: ParameterHelpOption[] = ['automatic', 'manual'];

export const getLanguageFromQuery = (): string => {
  const searchParams = new URLSearchParams(window.location.search);
  const l = searchParams.get('l');
  const supportedLanguage = supportedLanguages.find((x) => x.id === l);
  return supportedLanguage?.id || supportedLanguages[0].id;
};

export const defaultSettings: Settings = {
  languageId: getLanguageFromQuery(),
  fontSize: 16,
  defaultProjectName: 'default.py',
  usbVendorId: 0x0403, // This is the test board Vendor ID.
  baudRate: 115200,
  codeStructureHighlight: 'full',
  parameterHelp: 'automatic',
  showConnectHelp: true,
  showTransferHexHelp: true,
  showPostSaveHelp: true,
  showMultipleFilesHelp: true,
  allowEditingThirdPartyModules: false,
  warnForApiUnsupportedByDevice: true,
  robotsFTW: 'RobotsFTW',
};

const inContextTranslationLangId = 'lol';

export const isValidSettingsObject = (value: unknown): value is Settings => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const object = value as Record<string, unknown>;
  if (typeof object.languageId !== 'string' || !object.languageId) {
    return false;
  }
  if (object.languageId !== inContextTranslationLangId && !supportedLanguages.find((x) => x.id === object.languageId)) {
    return false;
  }
  if (codeStructureOptions.indexOf(object.codeStructureHighlight as CodeStructureOption) === -1) {
    return false;
  }
  if (parameterHelpOptions.indexOf(object.parameterHelp as ParameterHelpOption) === -1) {
    return false;
  }
  if (typeof object.showConnectHelp !== 'boolean') {
    return false;
  }
  if (typeof object.showTransferHexHelp !== 'boolean') {
    return false;
  }
  if (typeof object.showPostSaveHelp !== 'boolean') {
    return false;
  }
  if (typeof object.showMultipleFilesHelp !== 'boolean') {
    return false;
  }
  if (typeof object.allowEditingThirdPartyModules !== 'boolean') {
    return false;
  }
  return true;
};

export type CodeStructureOption = 'none' | 'full' | 'simple';
export const codeStructureOptions: CodeStructureOption[] = ['none', 'full', 'simple'];
export interface Settings {
  languageId: string;
  fontSize: number;
  defaultProjectName: string;
  usbVendorId: number;
  baudRate: number;
  codeStructureHighlight: CodeStructureOption;
  parameterHelp: ParameterHelpOption;
  allowEditingThirdPartyModules: boolean;
  showConnectHelp: boolean;
  showTransferHexHelp: boolean;
  showPostSaveHelp: boolean;
  showMultipleFilesHelp: boolean;
  warnForApiUnsupportedByDevice: boolean;
  robotsFTW: string;
}

export type SettingsContextValue = [Settings, (settings: Settings) => void];

export const SettingsContext = createContext<SettingsContextValue | undefined>(undefined);

export const useSettings = (): SettingsContextValue => {
  const settings = useContext(SettingsContext);
  if (!settings) {
    throw new Error('Missing provider');
  }
  return settings;
};
